import { isEmpty } from '../../../../../utils/utils';
import { caseCategoryOf } from './caseCategory';
import { ActAndSection, CaseSeverity, CaseDocument, LegalCaseDetails, PartyDetail } from './CaseDetailsDrawer/types';
import type { Case, CaseParty, CaseTag } from './legalCasesType';
import { isBackReference, isPlaceholderValue, normaliseText, withoutPlaceholders } from './placeholderValues';

const DOCUMENT_LABEL_PREFIX = 'View Document';
const CASE_SEVERITIES: CaseSeverity[] = ['high', 'medium', 'low'];

const definedValues = (values: (string | undefined)[]): string[] => values.filter((value): value is string => !isEmpty(value));

function orUndefined<T>(values: T[]): T[] | undefined {
    return values.length ? values : undefined;
}

const toText = (value?: string | null): string | undefined => normaliseText(value) || undefined;

const toCaseSummary = (tags: CaseTag[]): string | undefined =>
    orUndefined([...new Set(definedValues(tags.map((tag) => toText(tag.description))))])?.join('\n');

const toCaseThemes = (tags: CaseTag[]): string[] | undefined =>
    orUndefined(distinctIgnoringCase(tags.flatMap((tag) => tag.keyword?.split(',') ?? [])));

const toPartyNames = (parties: CaseParty[]): string[] | undefined => orUndefined(withoutPlaceholders(parties.map((party) => party.name)));

const distinctIgnoringCase = (values: (string | undefined)[]): string[] => {
    const firstSeen = new Map<string, string>();
    definedValues(values)
        .map(normaliseText)
        .forEach((value) => {
            if (!firstSeen.has(value.toLowerCase())) firstSeen.set(value.toLowerCase(), value);
        });
    return [...firstSeen.values()];
};

const resolveAdvocates = (parties: CaseParty[]): string[][] => {
    let inherited: string[] = [];
    return parties.map(({ advocate }) => {
        const named = withoutPlaceholders(advocate);
        if (named.length) {
            inherited = named;
            return named;
        }
        return (advocate ?? []).some(isBackReference) ? inherited : [];
    });
};

const toPartyDetails = (parties: CaseParty[], advocatesByParty: string[][]): PartyDetail[] | undefined =>
    orUndefined(
        parties.map<PartyDetail>(({ name, address }, index) => ({
            name: isPlaceholderValue(name) ? undefined : toText(name),
            address: address ?? undefined,
            advocates: advocatesByParty[index],
        })),
    );

const toActsAndSections = (acts: ActAndSection[] | null | undefined): ActAndSection[] | undefined =>
    orUndefined(
        (acts ?? []).map(({ underActs, underSections }) => ({
            underActs: toText(underActs),
            underSections: withoutPlaceholders(underSections),
        })),
    );

const toCaseDocuments = (docIds: string[] | undefined): CaseDocument[] =>
    definedValues(docIds ?? []).map((docId, index) => ({ label: `${DOCUMENT_LABEL_PREFIX} ${index + 1}`, docId }));

const toSeverity = (value: string | null | undefined): CaseSeverity | undefined => CASE_SEVERITIES.find((severity) => severity === value);

export const mapCaseToDetails = (row: Case): LegalCaseDetails => {
    const details = row.caseDetails;
    const tags = details?.standardTags ?? [];
    const petitionerParties = details?.petitionerAndAdvocate ?? [];
    const respondentParties = details?.respondentAndAdvocate ?? [];
    const petitionerAdvocates = resolveAdvocates(petitionerParties);
    const respondentAdvocates = resolveAdvocates(respondentParties);

    return {
        cino: row.cino ?? '',
        casetypeCaseNoCaseYr: row.caseNum,
        caseCategory: caseCategoryOf(row),
        standardCaseType: toText(row.standardCaseType),
        caseSummary: toCaseSummary(tags),
        petitioners: toPartyNames(petitionerParties),
        respondents: toPartyNames(respondentParties),
        advocateNames: orUndefined(distinctIgnoringCase([...petitionerAdvocates, ...respondentAdvocates].flat())),
        caseThemes: toCaseThemes(tags),
        filingDate: row.filingDate,
        caseStatus: row.status,
        nextHearingDate: row.nextHearingDate,
        decisionDate: row.decisionDate,
        caseOutcome: details?.standardDisposalRemark,
        natureOfDisposal: toText(details?.natureOfDisposal),
        stageOfCase: toText(details?.stageOfCase),
        severity: toSeverity(details?.severity_),
        civilCriminal: details?.civilCriminal ?? undefined,
        actsAndSections: toActsAndSections(details?.acts),
        firDetails: details?.firDetails ?? undefined,
        caseDocuments: toCaseDocuments(row.docId),
        court: row.court,
        presidingJudges: orUndefined(withoutPlaceholders(details?.judges)),
        courtEstablishment: toText(row.courtEstablishment),
        state: row.state,
        district: row.district,
        petitionerDetails: toPartyDetails(petitionerParties, petitionerAdvocates),
        respondentDetails: toPartyDetails(respondentParties, respondentAdvocates),
    };
};
